// src/modals/ModalPagoDetalle.js
import React, { useEffect, useState, useMemo } from "react";
import Modal from "antd/es/modal/Modal";
import { toast } from "react-toastify";
import { Image } from "antd";
import { updatePago, createPago } from "../services/pagosService";
import useAuth from "../hooks/useAuth";

/**
 * @param {{
 *  open: boolean;
 *  pago?: any | null;
 *  onClose: () => void;
 *  onUpdated?: (updated: any) => void;
 * }} props
 */
export function ModalPagoDetalle({ open, pago, onClose, onUpdated }) {
  const [comentario, setComentario] = useState("");
  const [loadingAction, setLoadingAction] = useState(null);

  const { user } = useAuth() || {};
  const confirmadoPorId = user?.id;

  const hasPago = !!pago?.id;

  useEffect(() => {
    if (pago) {
      setComentario(pago.comentario || "");
    } else {
      setComentario("");
    }
  }, [pago, open]);

  const title = useMemo(
    () => (pago ? `Detalle de pago` : "Detalle de pago"),
    [pago]
  );

  const formatter = useMemo(() => {
    try {
      return new Intl.NumberFormat("es-EC", {
        style: "currency",
        currency: "USD",
      });
    } catch {
      return { format: (n) => `$${Number(n || 0).toFixed(2)}` };
    }
  }, []);

  const formattedMonto = useMemo(() => {
    if (!pago) return "-";
    const montoValue = pago.productos?.monto ?? pago.monto ?? 0;
    return formatter.format(montoValue);
  }, [pago, formatter]);

  const isEfectivo = useMemo(() => {
    if (!pago?.tipo_pago) return false;
    return String(pago.tipo_pago).toLowerCase().trim() === "efectivo";
  }, [pago]);

  const representanteInfo = useMemo(() => {
    if (!pago) return null;
    const rep = pago.representantes || {};
    return {
      nombres: rep.nombres || pago.representante_nombre || "",
      apellidos: rep.apellidos || pago.representante_apellidos || "",
      cedula: rep.cedula || "",
      email: rep.email || "",
      telefono: rep.telefono || "",
    };
  }, [pago]);

  const deportistaInfo = useMemo(() => {
    if (!pago) return null;
    const dep = pago.deportistas || {};
    return {
      nombres: dep.nombres || pago.deportista_nombre || "",
      apellidos: dep.apellidos || pago.deportista_apellidos || "",
      cedula: dep.cedula || "",
    };
  }, [pago]);

  const colaboradorInfo = useMemo(() => {
    if (!pago) return null;
    const col = pago.colaboradores || {};
    
    const hasColaborador = 
      (col.nombres && col.nombres.trim()) || 
      (col.apellidos && col.apellidos.trim()) ||
      col.id ||
      pago.confirmado_por;
    
    if (!hasColaborador) return null;
    
    return {
      nombres: col.nombres || "",
      apellidos: col.apellidos || "",
      fechaConfirmacion: pago.fecha_confirmacion || null,
    };
  }, [pago]);


  const ESTADO_APROBADO = "paid";
  const ESTADO_RECHAZADO = "cancelled";

  const canChangeEstado = useMemo(() => {
    if (!pago) return false;
    if (pago.editable === false) return false;

    const raw = String(pago.estado || "").toLowerCase();
    return raw === "sended" || raw === "enviado";
  }, [pago]);

  const disableActions = !hasPago || loadingAction !== null || !canChangeEstado;

  async function handleChangeEstado(nextEstado, actionKey) {
    if (!pago?.id) return;

    if (!canChangeEstado) {
      toast.warn("Solo puedes aprobar si el pago ha sido enviado.");
      return;
    }

    setLoadingAction(actionKey);
    try {
      const nowISO = new Date().toISOString();

      const formToSend = {
        id: pago.id,
        representante_id: pago.representante_id,
        deportista_id: pago.deportista_id,
        producto_id: pago.producto_id,
        monto: pago.productos?.monto ?? pago.monto ?? 0,
        periodo: pago.periodo,
        fecha_vencimiento: pago.fecha_vencimiento,
        tipo_pago: (pago.tipo_pago || "").toLowerCase(),
        estado: nextEstado,
        comentario: comentario?.trim?.() || undefined,
        confirmado_por: confirmadoPorId || pago.confirmado_por || undefined,
        fecha_confirmacion: nowISO,
      };

      const updatedFromApi = await updatePago(formToSend);

      const mergedPago = {
        ...pago,
        ...updatedFromApi,
        estado: nextEstado,
        comentario: formToSend.comentario,
        confirmado_por: formToSend.confirmado_por,
        fecha_confirmacion: formToSend.fecha_confirmacion,
      };

      if (nextEstado === ESTADO_APROBADO) {
        toast.success("Pago aprobado correctamente");
      } else if (nextEstado === ESTADO_RECHAZADO) {
        toast.success("Pago rechazado correctamente. Generando nueva orden...");

        try {
          const newPagoPayload = {
            representante_id: pago.representante_id,
            deportista_id: pago.deportista_id,
            producto_id: pago.producto_id,
            monto: pago.productos?.monto ?? 0,
            periodo: pago.periodo,
            fecha_vencimiento: pago.fecha_vencimiento,
            estado: "pending",
            tipo_pago: null,
            comprobante_url: null,
            confirmado_por: null,
            fecha_confirmacion: null,
            comentario: null,
          };

          const nuevoPago = await createPago(newPagoPayload);
          console.log("[PAGOS][RECREATE] Nueva orden creada:", nuevoPago);
          toast.info("Se creó una nueva orden de pago con los mismos datos.");
        } catch (e) {
          console.error("[PAGOS][RECREATE][ERROR]", e);
          toast.error(
            "El pago fue rechazado, pero ocurrió un error al crear la nueva orden."
          );
        }
      }

      onUpdated && onUpdated(mergedPago);
      onClose();
    } catch (err) {
      console.error(err);
      toast.error(
        err?.message || "Ocurrió un error al actualizar el estado del pago"
      );
    } finally {
      setLoadingAction(null);
    }
  }

  const isSaving = loadingAction !== null;


  const comprobanteSrc = useMemo(() => {
    if (isEfectivo) return null;
    
    if (!pago?.comprobante_url) return null;

    const raw = pago.comprobante_url;

    if (typeof raw === "string" && (raw.startsWith("http://") || raw.startsWith("https://"))) {
      return raw;
    }

    if (typeof raw === "string" && raw.startsWith("data:image")) {
      return raw;
    }

    if (typeof raw === "string" && raw.trim()) {
      return `data:image/jpeg;base64,${raw}`;
    }

    return null;
  }, [pago, isEfectivo]);

  const lockMessage = useMemo(() => {
    if (!pago) return null;
    if (!canChangeEstado) {
      const raw = String(pago.estado || "").toLowerCase();
      if (raw === "paid" || raw === "aprobado") {
        return "Este pago ya fue aprobado y no puede modificarse.";
      }
      if (raw === "cancelled" || raw === "rechazado") {
        return "Este pago ya fue rechazado y no puede modificarse.";
      }
      if (pago.editable === false) {
        return "Este pago está bloqueado para edición.";
      }
      return "Solo se pueden modificar pagos en estado Pendiente.";
    }
    return null;
  }, [pago, canChangeEstado]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={
        <div className="flex items-center justify-between">
          <div className="text-base font-semibold">{title}</div>
          {pago?.estado && (
            <span className="rounded-full bg-slate-100 px-3 py-1 mr-4 text-xs font-medium text-slate-700 capitalize">
              Estado actual: {pago.estado}
            </span>
          )}
        </div>
      }
      footer={
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-slate-300 px-4 py-2 text-sm hover:bg-slate-100 transition disabled:opacity-60"
            disabled={isSaving}
          >
            Cerrar
          </button>

          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            {/* Mensaje de bloqueo si ya no es pendiente */}
            {lockMessage && (
              <span className="text-xs text-slate-500 text-left sm:text-right">
                {lockMessage}
              </span>
            )}

            <div className="flex items-center gap-2 justify-end">
              <button
                type="button"
                onClick={() => handleChangeEstado(ESTADO_RECHAZADO, "reject")}
                className="rounded-xl border border-red-500 text-red-600 px-4 py-2 text-sm hover:bg-red-50 transition disabled:opacity-60"
                disabled={disableActions}
              >
                {loadingAction === "reject" ? "Rechazando..." : "Rechazar"}
              </button>
              <button
                type="button"
                onClick={() => handleChangeEstado(ESTADO_APROBADO, "approve")}
                className="rounded-xl bg-emerald-600 text-white px-4 py-2 text-sm hover:bg-emerald-700 transition disabled:opacity-60"
                disabled={disableActions}
              >
                {loadingAction === "approve" ? "Aprobando..." : "Aprobar"}
              </button>
            </div>
          </div>
        </div>
      }
      centered
      width={720}
      destroyOnClose
      maskClosable={false}
      keyboard
      styles={{
        content: { borderRadius: 16 },
        body: {
          maxHeight: "70vh",
          overflowY: "auto",
          paddingRight: 8,
        },
      }}
    >
      {!pago ? (
        <div className="text-sm text-slate-500">
          No se encontró la información del pago.
        </div>
      ) : (
        <div className="space-y-6">
          {/* Sección: Representante, Deportista y Colaborador */}
          <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
              <h4 className="text-xs font-semibold text-slate-600 mb-2">
                Representante
              </h4>
              {representanteInfo && (
                <div className="space-y-1">
                  <p className="text-sm font-medium text-slate-900">
                    {`${representanteInfo.nombres} ${representanteInfo.apellidos}`.trim() || "—"}
                  </p>
                  {representanteInfo.cedula && (
                    <p className="text-xs text-slate-600">
                      <span className="font-medium">CI:</span> {representanteInfo.cedula}
                    </p>
                  )}
                  {representanteInfo.email && (
                    <p className="text-xs text-slate-600">
                      <span className="font-medium">Email:</span> {representanteInfo.email}
                    </p>
                  )}
                  {representanteInfo.telefono && (
                    <p className="text-xs text-slate-600">
                      <span className="font-medium">Teléfono:</span> {representanteInfo.telefono}
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
              <h4 className="text-xs font-semibold text-slate-600 mb-2">
                Deportista
              </h4>
              {deportistaInfo && (
                <div className="space-y-1">
                  <p className="text-sm font-medium text-slate-900">
                    {`${deportistaInfo.nombres} ${deportistaInfo.apellidos}`.trim() || "—"}
                  </p>
                  {deportistaInfo.cedula && (
                    <p className="text-xs text-slate-600">
                      <span className="font-medium">CI:</span> {deportistaInfo.cedula}
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className={`rounded-xl border px-3 py-3 ${
              colaboradorInfo 
                ? "border-emerald-200 bg-emerald-50" 
                : "border-red-200 bg-red-50"
            }`}>
              <h4 className={`text-xs font-semibold mb-2 ${
                colaboradorInfo ? "text-emerald-700" : "text-red-700"
              }`}>
                Confirmado por
              </h4>
              {colaboradorInfo ? (
                <div className="space-y-1">
                  <p className="text-sm font-medium text-emerald-900">
                    {`${colaboradorInfo.nombres} ${colaboradorInfo.apellidos}`.trim() || "—"}
                  </p>
                  {colaboradorInfo.fechaConfirmacion && (
                    <p className="text-xs text-emerald-700">
                      {new Date(colaboradorInfo.fechaConfirmacion).toLocaleString("es-EC", {
                        year: "numeric",
                        month: "short",
                        day: "2-digit",
                      })}
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-red-700 font-medium">Pendiente de confirmación</p>
              )}
            </div>
          </section>

          {/* Sección: Datos del pago */}
          <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Producto
              </label>
              <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm">
                {pago.productos?.nombre || pago.producto || "—"}
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Monto
              </label>
              <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold">
                {formattedMonto}
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Periodo
              </label>
              <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm">
                {pago.periodo || "—"}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Fecha vencimiento
                </label>
                <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm">
                  {pago.fecha_vencimiento
                    ? new Date(pago.fecha_vencimiento).toLocaleDateString(
                        "es-EC"
                      )
                    : "—"}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Tipo de pago
                </label>
                <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm capitalize">
                  {pago.tipo_pago || "—"}
                </div>
              </div>
            </div>
          </section>

          {/* Comprobante (imagen miniatura - siempre visible) */}
          <section>
            <h4 className="text-xs font-medium text-slate-600 mb-2">
              Comprobante
            </h4>
            <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 flex justify-center">
              {isEfectivo ? (
                <div className="text-sm text-slate-500 text-center py-8">
                  No se requiere comprobante para pagos en efectivo
                </div>
              ) : comprobanteSrc ? (
                <div className="max-h-96 max-w-full overflow-hidden rounded-lg border border-slate-200 bg-white">
                  <Image
                    src={comprobanteSrc}
                    alt="Comprobante de pago"
                    style={{ maxHeight: "300px", objectFit: "contain" }}
                    fallback="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='220' height='160' viewBox='0 0 220 160'%3E%3Crect width='220' height='160' rx='16' fill='%23f8fafc'/%3E%3Crect x='32' y='26' width='156' height='108' rx='10' fill='%23e5e7eb'/%3E%3Cpath d='M52 54h116M52 74h80M52 94h60' stroke='%239ca3af' stroke-width='4' stroke-linecap='round'/%3E%3Ccircle cx='158' cy='100' r='12' fill='%23ffffff' stroke='%236b7280' stroke-width='2'/%3E%3Cpath d='M152 100l4 4 8-8' stroke='%2365a30d' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E"
                  />
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-8">
                  <div className="w-48 h-32 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 flex items-center justify-center mb-3">
                    <svg
                      className="w-12 h-12 text-slate-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  <p className="text-sm text-slate-500 text-center">
                    No se ha subido ningún comprobante aún
                  </p>
                </div>
              )}
            </div>
          </section>
        </div>
      )}
    </Modal>
  );
}
